import React from "react";
import { InlineMath, BlockMath } from "react-katex";

import MaturaFunctionGraph, {
  MATURA_FUNCTION_GRAPH_RANGE,
} from "../../../../components/GraphPlotter/MaturaFunctionGraph";
import FractionInput from "../../../../components/inputs/FractionInput";
import EquationInput from "../../../../components/inputs/EquationInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";


/*
  Zadanie maturalne - otwarte (0-2 pkt)
  Wyznaczanie postaci kanonicznej funkcji kwadratowej na podstawie:
  - wierzchołka (h, k),
  - punktu przecięcia z osią Oy: (0, c).

  Punktacja:
  - 1 pkt: poprawny współczynnik a,
  - 1 pkt: poprawny wzór w postaci kanonicznej.
*/

const EPS = 1e-9;

function randInt(min, max, exclude = new Set()) {
  const span = max - min + 1;
  let v;
  do {
    v = Math.floor(Math.random() * span) + min;
  } while (exclude.has(v));
  return v;
}

function getRaw(val) {
  if (typeof val === "string") return val;
  if (val && typeof val === "object") {
    if (val.raw && typeof val.raw === "object") return getRaw(val.raw);
    for (const k of ["raw", "value", "text", "expr", "equation", "latex"]) {
      if (typeof val[k] === "string" && val[k].trim() !== "") return val[k];
    }
    return "";
  }
  return String(val ?? "");
}

function parseNumberLike(rawValue) {
  if (rawValue == null) return null;

  let raw =
    typeof rawValue === "object" && rawValue.raw != null
      ? String(rawValue.raw)
      : String(rawValue);

  raw = raw.trim().replace(/\s+/g, "").replace(",", ".").replace(/\u2212/g, "-");
  if (!raw) return null;

  const frac = raw.match(/^([+\-]?\d+)\s*\/\s*([+\-]?\d+)$/);
  if (frac) {
    const p = parseInt(frac[1], 10);
    const q = parseInt(frac[2], 10);
    if (!q) return null;
    return p / q;
  }

  if (/^[+\-]?\d*\.?\d+(?:e[+\-]?\d+)?$/i.test(raw)) {
    const v = Number(raw);
    return Number.isFinite(v) ? v : null;
  }

  return null;
}

function normalizeMixedFractions(s) {
  return s.replace(
    /([+\-]?)\s*(\d+)\s+(\d+)\s*\/\s*(\d+)/g,
    (_, sign, whole, num, den) => {
      const W = parseInt(whole, 10);
      const N = parseInt(num, 10);
      const D = parseInt(den, 10);
      if (!D) return _;
      const improperAbs = W * D + N;
      const signChar = sign === "-" ? "-" : sign === "+" ? "+" : "";
      return `${signChar}${improperAbs}/${D}`;
    },
  );
}

function normalizeCanonicalRHS(raw) {
  let s = getRaw(raw);
  if (!s) return "";

  s = s
    .replace(/\\left\(/g, "(")
    .replace(/\\right\)/g, ")")
    .replace(/\\cdot/g, "")
    .replace(/\^{\s*2\s*}/g, "^2")
    .replace(/\\,/g, "")
    .replace(/\\ /g, "")
    .replace(/\\!/g, "")
    .replace(/\\-/g, "-");

  s = s.replace(/(\d),(\d)/g, "$1.$2");
  s = normalizeMixedFractions(s);
  s = s
    .replace(/\u2212/g, "-")
    .replace(/^f\(x\)=/i, "")
    .replace(/^y=/i, "")
    .replace(/\s+/g, "");

  return s;
}

function parseNumberStrict(s) {
  if (s == null) return null;
  s = s.trim();
  if (!s) return null;
  s = s.replace(/(\d),(\d)/g, "$1.$2");

  if (/^[+\-]?\d*\.?\d+(?:e[+\-]?\d+)?$/i.test(s)) return parseFloat(s);

  const m = s.match(/^([+\-]?\d+)\s*\/\s*([+\-]?\d+)$/);
  if (m) {
    const p = parseInt(m[1], 10);
    const q = parseInt(m[2], 10);
    if (!q) return null;
    return p / q;
  }
  return null;
}

const NUM = "(?:\\d+\\/\\d+|\\d*\\.\\d+|\\d+)";
function parseCanonicalUserRaw(raw) {
  const s = normalizeCanonicalRHS(raw);
  if (!s) return null;

  const re = new RegExp(
    `^([+\\-]?${NUM}|[+\\-])?\\(x([+-])(${NUM})\\)\\^2(?:([+-])(${NUM}))?$`,
    "i",
  );
  const m = s.match(re);
  if (!m) return null;

  let a;
  const aStr = m[1];
  if (aStr == null || aStr === "" || aStr === "+") a = 1;
  else if (aStr === "-") a = -1;
  else {
    a = parseNumberStrict(aStr);
    if (a == null) return null;
  }

  const signP = m[2] === "+" ? +1 : -1;
  const pNum = parseNumberStrict(m[3]);
  if (pNum == null) return null;
  const h = signP === +1 ? -pNum : +pNum;

  let k = 0;
  if (m[4] && m[5]) {
    const signK = m[4] === "+" ? +1 : -1;
    const kNum = parseNumberStrict(m[5]);
    if (kNum == null) return null;
    k = signK * kNum;
  }

  return { a, h, k };
}

function explicitMultiply(expr) {
  let s = expr;
  s = s.replace(/(\d)(x)/g, "$1*$2");
  s = s.replace(/(\d)\(/g, "$1*(");
  s = s.replace(/\)(\d|x)/g, ")*$1");
  s = s.replace(/x\(/g, "x*(");
  return s;
}

function safeEvalRHS(rhs, x) {
  let s = normalizeCanonicalRHS(rhs);
  if (!s) return NaN;
  s = explicitMultiply(s).replace(/\^/g, "**");
  const js = s.replace(/x/g, `(${x})`);
  try {
    // eslint-disable-next-line no-new-func
    const val = Function(`\"use strict\"; return (${js});`)();
    return Number.isFinite(val) ? val : NaN;
  } catch {
    return NaN;
  }
}

function numericallyEquivalentToCanonical(userRHS, a, h, k, tol = 1e-3) {
  const xs = [-4, -2, -1, 0, 1, 2, 4];
  for (const x of xs) {
    const u = safeEvalRHS(userRHS, x);
    if (!Number.isFinite(u)) return false;
    const ref = a * (x - h) * (x - h) + k;
    if (Math.abs(u - ref) > tol) return false;
  }
  return true;
}

function numberToLatex(v) {
  const rounded = Math.round(v);
  if (Math.abs(v - rounded) < 1e-9) return String(rounded);

  for (const d of [2, 3, 4, 5, 6]) {
    const n = Math.round(v * d);
    if (Math.abs(v - n / d) < 1e-9) {
      const sign = n < 0 ? "-" : "";
      return `${sign}\\frac{${Math.abs(n)}}{${d}}`;
    }
  }

  return String(v).replace(".", "{,}");
}

function coeffToLatex(a) {
  if (Math.abs(a - 1) < 1e-9) return "";
  if (Math.abs(a + 1) < 1e-9) return "-";
  return numberToLatex(a);
}

function canonicalBaseLatex(a, h) {
  const aLatex = coeffToLatex(a);
  if (h === 0) return `${aLatex}x^{2}`;
  const sign = h < 0 ? "+" : "-";
  const absH = Math.abs(h);
  return `${aLatex}(x ${sign} ${absH})^{2}`;
}

function canonicalFullLatex(a, h, k) {
  const base = canonicalBaseLatex(a, h);
  if (k === 0) return `f(x) = ${base}`;
  const sign = k < 0 ? "-" : "+";
  const absK = Math.abs(k);
  return `f(x) = ${base} ${sign} ${absK}`;
}

export function buildParabolaCurve({ a, h, k, xRange }) {
  const [xMin, xMax] = xRange;
  const sampleCount = 120;
  const x = Array.from(
    { length: sampleCount + 1 },
    (_, index) => xMin + ((xMax - xMin) * index) / sampleCount
  );
  return {
    x,
    y: x.map((value) => a * (value - h) ** 2 + k),
  };
}

function generateParams() {
  // Zawsze zapewniamy miejsca zerowe: q = -a*r^2.
  const combos = [
    { r: 1, aAbs: 1 },
    { r: 1, aAbs: 2 },
    { r: 2, aAbs: 1 },
  ];

  for (let i = 0; i < 900; i++) {
    const h = randInt(-3, 3, new Set([0]));
    const { r, aAbs } = combos[randInt(0, combos.length - 1)];
    const up = Math.random() < 0.5;

    const a = (up ? 1 : -1) * aAbs;
    const k = -a * r * r;
    const c = a * h * h + k;

    const x1 = h - r;
    const x2 = h + r;
    if (x1 < -5 || x2 > 5) continue;
    if (!Number.isInteger(c) || c < -5 || c > 5) continue;

    const inRangeCount = Array.from({ length: 11 }, (_, idx) => idx - 5)
      .map((x) => a * (x - h) * (x - h) + k)
      .filter((y) => y >= -5 && y <= 5).length;

    if (inRangeCount < 6) continue;

    return { a, h, k, c };
  }

  return { a: -1, h: 1, k: 4, c: 3 };
}

function generateMay2025OriginalTask() {
  return {
    type: "MaturaQuadraticCanonicalFromGraphOpenTask",
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: "12.1",
    },
    data: {
      a: -1 / 3,
      h: 3,
      k: 6,
      c: 3,
      maxPoints: 2,
      xRange: [...MATURA_FUNCTION_GRAPH_RANGE],
      yRange: [...MATURA_FUNCTION_GRAPH_RANGE],
    },
  };
}

export function generateTask(options = {}) {
  if (options.variant === "original" && options.session === "2025_MAJ") {
    return generateMay2025OriginalTask();
  }

  const { a, h, k, c } = generateParams();

  return {
    type: "MaturaQuadraticCanonicalFromGraphOpenTask",
    data: {
      a,
      h,
      k,
      c,
      maxPoints: 2,
      xRange: [...MATURA_FUNCTION_GRAPH_RANGE],
      yRange: [...MATURA_FUNCTION_GRAPH_RANGE],
    },
  };
}

export function renderTask({ data }) {
  const { h, k, c, a, xRange, yRange } = data;

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - otwarte (0-2 pkt).</strong>
      </p>

      <p>
        W kartezjańskim układzie współrzędnych{" "}
        <InlineMath math={"(x,y)"} /> przedstawiono fragment paraboli, która jest
        wykresem funkcji kwadratowej <InlineMath math={"f"} /> (zobacz rysunek).
      </p>

      <p>
        Wierzchołek tej paraboli ma współrzędne{" "}
        <InlineMath math={`(p,q)=(${h},\\;${k})`} />.
        Ta parabola przecina oś <InlineMath math={"Oy"} /> w punkcie{" "}
        <InlineMath math={`(0,\\;${c})`} />.
      </p>

      <MaturaFunctionGraph
        curves={[buildParabolaCurve(data)]}
        xRange={xRange}
        yRange={yRange}
      />

      <p>
        Wyznacz wzór funkcji <InlineMath math={"f"} /> w postaci kanonicznej.
        Zapisz obliczenia.
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};
  const setField = (key) => (val) => onChange({ ...ua, [key]: val });
  const rawA = getRaw(ua.a);
  const rawCanon = getRaw(ua.canonical);

  return (
    <div style={{ display: "grid", gap: 12, maxWidth: 560 }}>
      <OpenTaskScoringHint variant="independent" />
      <div style={{ display: "grid", gap: 6 }}>
        <div>
          <strong>Krok 1.</strong> Oblicz współczynnik <InlineMath math={"a"} />:
        </div>
        <div className="max-w-[260px]">
          <FractionInput
            value={ua.a}
            onChange={setField("a")}
            disabled={disabled}
            placeholder="np. -1"
            label="a"
          />
        </div>
        {rawA ? (
          <div>
            <strong>Podgląd:</strong> <InlineMath math={`a=${rawA}`} />
          </div>
        ) : null}
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        <div>
          <strong>Krok 2.</strong> Zapisz wzór funkcji w postaci kanonicznej{" "}
          <InlineMath math={"f(x)=a(x-p)^2+q"} /> (wpisz prawą stronę):
        </div>
        <div className="flex items-center gap-2">
          <InlineMath math={"f(x)="} />
          <EquationInput
            value={ua.canonical}
            onChange={setField("canonical")}
            disabled={disabled}
            placeholder="np. -1(x-3)^2+6"
            showPreview={false}
            ariaLabel="postać kanoniczna funkcji kwadratowej"
          />
        </div>
        {rawCanon ? (
          <div>
            <strong>Podgląd:</strong> <InlineMath math={`f(x)=${rawCanon}`} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const { a, h, k, maxPoints } = data;

  const aVal = parseNumberLike(userAnswer.a);
  const aOk = aVal != null && Math.abs(aVal - a) < EPS;

  let canonicalOk = false;
  const parsed = parseCanonicalUserRaw(userAnswer.canonical);
  if (parsed) {
    canonicalOk =
      Math.abs(parsed.a - a) < EPS &&
      Math.abs(parsed.h - h) < EPS &&
      Math.abs(parsed.k - k) < EPS;
  } else if (userAnswer.canonical != null) {
    canonicalOk = numericallyEquivalentToCanonical(userAnswer.canonical, a, h, k);
  }

  let score = 0;
  if (canonicalOk) score = maxPoints;
  else if (aOk) score = 1;

  return {
    isCorrect: score === maxPoints,
    score,
    maxScore: maxPoints,
    parts: { aOk, canonicalOk },
  };
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  const rawA = getRaw(userAnswer.a);
  const rawCanon = getRaw(userAnswer.canonical);

  return (
    <div style={{ display: "grid", gap: 4, textAlign: "left" }}>
      <div>
        <InlineMath math={`a=${rawA || "\\text{–}"}`} />
      </div>
      <div>
        <InlineMath math={`f(x)=${rawCanon || "\\text{–}"}`} />
      </div>
    </div>
  );
}

export function renderAnswer({ data }) {
  const { a, h, k } = data;
  return (
    <div style={{ display: "grid", gap: 4, textAlign: "left" }}>
      <div>
        <InlineMath math={`a=${numberToLatex(a)}`} />
      </div>
      <div>
        <InlineMath math={canonicalFullLatex(a, h, k)} />
      </div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const { a, h, k, c } = data;
  const aLatex = numberToLatex(a);
  const hh = h * h;

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Korzystamy z postaci kanonicznej:
      </p>
      <BlockMath math={"f(x)=a(x-p)^2+q"} />
      <p>
        Punkt <InlineMath math={`(0,${c})`} /> należy do wykresu, więc:
      </p>
      <BlockMath math={`${c}=a(0${h < 0 ? "+" : "-"}${Math.abs(h)})^2${k < 0 ? "-" : "+"}${Math.abs(k)}`} />
      <BlockMath math={`${c}=${hh}a${k < 0 ? "-" : "+"}${Math.abs(k)}`} />
      <BlockMath math={`${c - k}=${hh}a`} />
      <BlockMath math={`a=${aLatex}`} />

      <p>
        <strong>Krok 2.</strong> Podstawiamy <InlineMath math={"a"} />,{" "}
        <InlineMath math={"p"} /> i <InlineMath math={"q"} /> do postaci
        kanonicznej:
      </p>
      <BlockMath math={canonicalFullLatex(a, h, k)} />
    </div>
  );
}
